/**
 * O registro do formulário de plano: valores iniciais e leitura de um plano
 * que veio da API.
 *
 * Existe porque este objeto estava escrito TRÊS vezes no index (estado inicial
 * do formulário, estado da tela e o "cancelar"), e um campo novo de plano
 * precisa aparecer nos três. Faltar num deles não dá erro: o campo
 * simplesmente não entra no `initialValues` do Formik, não é submetido, e o
 * valor que estava salvo é apagado na primeira edição do plano.
 */

/**
 * Os recursos liga/desliga do plano, na ordem em que aparecem no formulário.
 *
 * Vive aqui, e não no JSX, porque é a lista que o formulário percorre para
 * desenhar os campos: recurso que sair daqui desaparece da tela sem erro
 * nenhum, e o plano passa a ser salvo sem ele. O teste prende esta lista ao
 * registro do formulário, nos dois sentidos.
 */
export const PLAN_FEATURES = [
    'useWhatsapp',
    'useWhatsappOfficial',
    'useFacebook',
    'useInstagram',
    'useCampaigns',
    'useSchedules',
    'useInternalChat',
    'useExternalApi',
    'useKanban',
    'useIntegrations',
    'wavoip'
]

/** Os tipos de adicional, na ordem em que aparecem no seletor (#4377). */
export const ADDON_TYPES = ['user', 'connection', 'storage']

/** As unidades em que um adicional pode ser vendido. */
export const ADDON_UNITS = ['UN', 'GB']

/** Unidade natural de cada tipo — a mesma regra do backend. */
const UNIDADE_PADRAO = { user: 'UN', connection: 'UN', storage: 'GB' }

/**
 * Linha nova da lista de adicionais.
 *
 * Nasce sem `id`: é isso que diz ao backend "este item não existe ainda". A
 * descrição vem em branco de propósito — item sem descrição é descartado ao
 * salvar, então quem clicou e desistiu não cria lixo.
 */
export function novoAddon(type = 'user') {
    return { type, description: '', quantity: 1, unit: UNIDADE_PADRAO[type] || 'UN', unitAmount: '' }
}

/**
 * Lê o valor como o usuário digita: "1,50", "1.234,56", "R$ 1,50".
 *
 * MESMA regra do backend (`services/PlanService/planAddon.ts#parseMoney`): o
 * último separador é o decimal, os anteriores são de milhar. Tem de ser a
 * mesma, senão o total que a tela mostra não é o que o backend grava — e a
 * diferença só aparece na fatura do cliente.
 */
export function parseMoney(valor) {
    const bruto = String(valor ?? '').replace(/[^\d.,-]/g, '')
    if (!bruto) return 0

    const ultimo = Math.max(bruto.lastIndexOf(','), bruto.lastIndexOf('.'))
    let inteiro = bruto
    let decimais = ''

    if (ultimo >= 0) {
        const casas = bruto.length - ultimo - 1
        // Três casas depois do separador é milhar, não centavo ("1.500").
        if (casas > 0 && casas <= 2) {
            inteiro = bruto.slice(0, ultimo)
            decimais = bruto.slice(ultimo + 1)
        }
    }

    const numero = Number(`${inteiro.replace(/[.,]/g, '')}.${decimais || '0'}`)
    return Number.isFinite(numero) ? Math.max(0, numero) : 0
}

/**
 * O total da linha: quantidade x valor unitário.
 *
 * Em centavos, porque 3 x 1,10 em ponto flutuante dá 3.3000000000000003 — e na
 * tela isso apareceria.
 */
export function addonTotal(quantity, unitAmount) {
    const qtd = Math.floor(Number(quantity))
    const vezes = Number.isFinite(qtd) && qtd > 0 ? qtd : 1
    return (Math.round(parseMoney(unitAmount) * 100) * vezes) / 100
}

/** Soma dos adicionais do plano — o que o cliente pagaria levando tudo. */
export function addonsTotal(addons) {
    if (!Array.isArray(addons)) return 0
    return addons.reduce((soma, item) => soma + addonTotal(item?.quantity, item?.unitAmount), 0)
}

/** Plano em branco — o que a tela mostra antes de escolher um registro. */
export const PLAN_RECORD_PADRAO = {
    name: '',
    users: 0,
    connections: 0,
    queues: 0,
    storageGb: 0,
    amount: 0,
    useWhatsapp: true,
    useFacebook: true,
    useInstagram: true,
    useCampaigns: true,
    useSchedules: true,
    useInternalChat: true,
    useExternalApi: true,
    useKanban: true,
    useIntegrations: true,
    isPublic: true,
    useWhatsappOfficial: false,
    trial: false,
    trialDays: 3,
    recurrence: 'monthly',
    wavoip: false,
    addons: []
}

/**
 * Recurso ligado quando o plano não diz nada.
 *
 * Mantém o comportamento que a tela já tinha: só `false` explícito desliga, e
 * `undefined` (plano salvo antes do recurso existir) conta como ligado.
 */
const ligado = valor => valor !== false

/** Número do plano, tolerando nulo e texto — campo que nunca pode virar NaN. */
const numero = valor => Number(valor) || 0

/** Converte o plano que veio da API no registro que o formulário edita. */
export function planRecordFrom(data) {
    return {
        id: data.id,
        name: data.name || '',
        users: numero(data.users),
        connections: numero(data.connections),
        queues: numero(data.queues),
        // Plano salvo antes da coluna existir chega sem o campo: vira 0, e não
        // `undefined`, senão o campo numérico some do submit (#4459).
        storageGb: numero(data.storageGb),
        amount: data.amount?.toLocaleString('pt-br', { minimumFractionDigits: 2 }) || 0,
        useWhatsapp: ligado(data.useWhatsapp),
        useWhatsappOfficial: ligado(data.useWhatsappOfficial),
        useFacebook: ligado(data.useFacebook),
        useInstagram: ligado(data.useInstagram),
        useCampaigns: ligado(data.useCampaigns),
        useSchedules: ligado(data.useSchedules),
        useInternalChat: ligado(data.useInternalChat),
        useExternalApi: ligado(data.useExternalApi),
        useKanban: ligado(data.useKanban),
        useIntegrations: ligado(data.useIntegrations),
        wavoip: ligado(data.wavoip),
        isPublic: data.isPublic,
        trial: data.trial,
        trialDays: data.trialDays,
        recurrence: data.recurrence,
        addons: addonsFrom(data.addons)
    }
}

/**
 * Os adicionais que vieram da API, no formato que os campos editam.
 *
 * O valor unitário vira TEXTO: o campo é de texto (aceita "1,50") e o pg
 * devolve DECIMAL como string. Converter para número aqui faria "15.00"
 * aparecer como 15 e perder o centavo na tela.
 */
function addonsFrom(addons) {
    if (!Array.isArray(addons)) return []
    return addons.map(item => ({
        id: item.id,
        type: item.type || 'user',
        description: item.description || '',
        quantity: numero(item.quantity) || 1,
        unit: item.unit || UNIDADE_PADRAO[item.type] || 'UN',
        unitAmount: item.unitAmount != null ? String(item.unitAmount) : ''
    }))
}
